import { useCallback, useEffect, useState } from "react";
import { ReleaseNotesData } from "../types/releaseNotes";

const LAST_SEEN_VERSION_KEY = "costaff_last_seen_version";

interface UseReleaseNotesReturn {
  releaseNotes: ReleaseNotesData | null;
  hasNewReleases: boolean;
  markAsSeen: () => void;
  isLoading: boolean;
  error: string | null;
}

export function useReleaseNotes(): UseReleaseNotesReturn {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNotesData | null>(null);
  const [hasNewReleases, setHasNewReleases] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load release notes from JSON file
  const loadReleaseNotes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Determine which release notes file to load based on environment
      const environment = getCurrentEnvironment();
      const fileName = environment === "prod" ? "/release-notes.prod.json" : "/release-notes.json";

      const response = await fetch(fileName);
      if (!response.ok) {
        throw new Error(`Failed to load release notes: ${response.status}`);
      }

      const data: ReleaseNotesData = await response.json();
      setReleaseNotes(data);

      // Check if there are new releases
      const lastSeenVersion = localStorage.getItem(LAST_SEEN_VERSION_KEY);
      if (!lastSeenVersion || lastSeenVersion !== data.currentVersion) {
        setHasNewReleases(true);
      } else {
        setHasNewReleases(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load release notes");
      console.error("Error loading release notes:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Mark current version as seen
  const markAsSeen = useCallback(() => {
    if (releaseNotes) {
      localStorage.setItem(LAST_SEEN_VERSION_KEY, releaseNotes.currentVersion);
      setHasNewReleases(false);
    }
  }, [releaseNotes]);

  // Load release notes on mount
  useEffect(() => {
    loadReleaseNotes();
  }, [loadReleaseNotes]);

  return {
    releaseNotes,
    hasNewReleases,
    markAsSeen,
    isLoading,
    error,
  };
}

// Helper function to get environment from window location
export function getCurrentEnvironment(): string {
  if (typeof window === "undefined") return "unknown";

  const hostname = window.location.hostname;

  if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
    return "dev";
  } else if (hostname.includes("demo") || hostname.includes("staging")) {
    return "demo";
  } else {
    return "prod";
  }
}

// Helper function to format version display
export function formatVersionDisplay(version: string, environment: string): string {
  return `v${version} (${environment})`;
}
